/* eslint-disable no-self-assign */
import { useContext, useMemo } from 'react';
import axios from 'axios';
import AuthContext from '../../secure-route/AuthContext';
import global from '../../../config/Global.json';
import Swal from 'sweetalert2';

const useApi = () => {
  const { authToken, user } = useContext(AuthContext);
  const token = authToken?.access || null;
  const userId = user?.user_id || user?.id || '';

  return useMemo(() => {
  const getAPI = (API_NAME) => global.api.host + global.api[API_NAME];

  const getHost = () => global.api.host;

  const authHeaders = (extraHeaders = {}) => ({
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  });

  const generateCacheKey = (api) => `cache_${userId}_${api}`;

  const Post = async (api, payload) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);
    try {
      if (api === 'createSalesInvoice' && payload?.sales_invoice?.invoice_type !== 'ecom') {
        const shift = await axios.get(getAPI('isShiftActive'), { headers: authHeaders() });
        if (!shift.data?.shift_id) {
          const error = new Error('Start your shift before recording a sale.');
          error.response = { status: 409, data: { detail: error.message, code: 'active_shift_required' } };
          throw error;
        }
      }
      const response = await axios.post(_api, payload, {
        headers: authHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const Put = async (api, id, payload) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);
    try {
      // Check if id already contains query parameters
      const hasQueryParams = id.includes('?');
      const url = hasQueryParams ? `${_api}${id.replace('?', '/?')}` : `${_api}${id}/`;
      const response = await axios.put(url, payload, {
        headers: authHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const Patch = async (api, id, payload) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);
    try {
      // Check if id already contains query parameters
      const idStr = String(id);
      const hasQueryParams = idStr.includes('?');
      const url = hasQueryParams ? `${_api}${idStr.replace('?', '/?')}` : `${_api}${idStr}/`;
      const response = await axios.patch(url, payload, {
        headers: authHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const Get = async (api, extraHeaders = {}) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);
    try {
      const response = await axios.get(_api, {
        headers: authHeaders(extraHeaders),
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const _Get = async (api) => {
    const key = generateCacheKey(api);

    try {
      const resp = await Get(api);
      localStorage.setItem(key, JSON.stringify(resp));
      return resp;
    } catch (error) {
      console.error("Error in _Get:", error);
      throw error;
    }
  };


  const _Post = async (api, payload) => {
    const key = generateCacheKey(api);
    let cachedResponse = localStorage.getItem(key);

    if (cachedResponse !== null) {
      return JSON.parse(cachedResponse);
    } else {
      try {
        const resp = await Post(api, payload);
        localStorage.setItem(key, JSON.stringify(resp.data));
        return resp.data;
      } catch (error) {
        console.error("Error in _Post:", error);
        throw error;
      }
    }
  };

  const refreshCache = (api, method) => {
    const key = generateCacheKey(api);
    localStorage.removeItem(key);

    // Immediately fetch and cache new data
    if (method.toLowerCase() === "get") {
      _Get(api).catch(console.error);
    } else {
      _Post(api).catch(console.error);
    }
  };

  const clearCache = (cacheOf) => {
    if (cacheOf === "all") {
      Swal.fire({
        title: 'Clear Cache',
        text: 'Are you sure you want to clear cache and reload the page?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, clear it!',
        cancelButtonText: 'Cancel'
      }).then((result) => {
        if (result.isConfirmed) {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith("cache_")) {
              localStorage.removeItem(key);
            }
          });
          window.location.href = window.location.href;
        }
      });
    } else {
      const fullKey = generateCacheKey(cacheOf);
      localStorage.removeItem(fullKey);
      window.location.reload();
    }
  };

  const Delete = async (api, id) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);
    try {
      // Check if id already contains query parameters
      const hasQueryParams = id.includes('?');
      const url = hasQueryParams ? `${_api}${id.replace('?', '/?')}` : `${_api}${id}/`;
      const response = await axios.delete(url, {
        headers: authHeaders(),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const PostFormData = async (api, payload) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);

    let customHeaders = authHeaders();

    if (payload instanceof FormData) {
      delete customHeaders["Content-Type"]; // axios will handle boundary
    }

    try {
      const response = await axios.post(_api, payload, {
        headers: customHeaders,
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const PostWithId = async (api, id, payload) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);

    const headers = {};

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await axios.post(`${_api}${id}`, payload, {
        headers,
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const getFromGlobalConfig = (obj) => {
    return global.obj;
  }

  const GetPaginatedData = async (
    api,
    options = {
      page: 1,
      search: "",
      queryParams: {},
      extraHeaders: {},
    }
  ) => {
    const { page = 1, search = "", queryParams = {}, extraHeaders = {} } = options;

    // Validate input parameters
    if (page < 0) {
      console.warn("Invalid page number:", page);
      return {
        data: [],
        totalCount: 0,
        nextPage: null,
        previousPage: null,
      };
    }

    let _api = getAPI(api).includes("undefined") ? api : getAPI(api);

    const params = new URLSearchParams();

    // Only add page parameter if it's greater than 1
    if (page > 1) params.append("page", String(page));

    // Only add search parameter if it's not empty
    if (search && search.trim()) params.append("search", search.trim());

    // Add query parameters
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    });

    const fullUrl = `${_api}?${params.toString()}`;
    const headers = {};

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    Object.assign(headers, extraHeaders);

    try {
      const response = await axios.get(fullUrl, { headers });
      // Handle different response structures
      const responseData = response.data;

      // Check if response has the expected structure
      if (!responseData) {
        console.warn("Empty response from API");
        return {
          data: [],
          totalCount: 0,
          nextPage: null,
          previousPage: null,
        };
      }

      // Handle both array and paginated object responses
      let results, totalCount, nextPage, previousPage;

      if (Array.isArray(responseData)) {
        // If response is an array, treat it as a simple list
        results = responseData;
        totalCount = responseData.length;
        nextPage = null;
        previousPage = null;
      } else if (responseData.results) {

        results = responseData.results || [];
        totalCount = responseData.count || 0;
        nextPage = responseData.next || null;
        previousPage = responseData.previous || null;
      } else {

        results = responseData.data || [];
        totalCount = responseData.total || responseData.totalCount || 0;
        // Standard paginated response structure
        results = responseData.results || responseData.data || responseData.orders || [];
        totalCount = responseData.count || responseData.total || responseData.totalCount || (results ? results.length : 0);
        nextPage = responseData.next || null;
        previousPage = responseData.previous || null;
      }

      return {
        data: results,
        totalCount,
        nextPage,
        previousPage,
        rawResponse: responseData,
      };
    } catch (err) {
      console.error("❌ Error in GetPaginatedData:", err);
      console.error("🔗 API URL:", fullUrl);
      console.error("⚠️ Error details:", err.response?.data || err.message);
      throw err;
    }
  };

  const PostPaginatedData = async (
    api,
    options = {
      page: 1,
      search: "",
      queryParams: {},
      body: {},
    }
  ) => {
    const { page = 1, search = "", queryParams = {}, body = {} } = options;

    if (page === 0) {
      return {
        data: [],
        totalCount: 0,
        nextPage: null,
        previousPage: null,
      };
    }

    let _api = getAPI(api).includes("undefined") ? api : getAPI(api);

    const params = new URLSearchParams();

    if (page > 1) params.append("page", String(page));
    if (search.trim()) params.append("search", search.trim());

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const fullUrl = `${_api}?${params.toString()}`;
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await axios.post(fullUrl, body, { headers });

      return {
        data: response.data.results,
        totalCount: response.data.count,
        nextPage: response.data.next,
        previousPage: response.data.previous,
      };
    } catch (err) {
      throw err;
    }
  };

  const PostDataForm = async (api, formData) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);
    try {
      const response = await axios.post(_api, formData, {
        headers: authHeaders({ 'Content-Type': 'multipart/form-data' }),
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const DownloadFile = async (url) => {
    let _url = url.startsWith('http') ? url : `${getHost()}/${url}`;
    try {
      const response = await axios.get(_url, {
        headers: authHeaders({ Accept: '*/*' }),
        responseType: 'blob'
      });
      return response;
    } catch (err) {
      throw err;
    }
  };

  const PatchFormData = async (api, id, formData) => {
    let _api = (getAPI(api).includes('undefined')) ? api : getAPI(api);
    try {
      const idStr = String(id);
      const hasQueryParams = idStr.includes('?');
      const url = hasQueryParams ? `${_api}${idStr.replace('?', '/?')}` : `${_api}${idStr}/`;
      const response = await axios.patch(url, formData, {
        headers: authHeaders({ 'Content-Type': 'multipart/form-data' }),
      });
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  return {
    Post,
    Put,
    Patch,
    Get,
    Delete,
    PostFormData,
    PostWithId,
    getAPI,
    getHost,
    _Get,
    _Post,
    refreshCache,
    getFromGlobalConfig,
    clearCache,
    GetPaginatedData,
    PostPaginatedData,
    PostDataForm,
    DownloadFile,
    PatchFormData,
  };
  }, [token, userId]);
};

export default useApi;
